import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Plus } from "lucide-react";
import moment from "moment";
import { useNavigate } from "react-router-dom";

import { DraftPackService } from "../../services/draftPackService";
import Alert from "../components/Alert";
import { PageHeader } from "../components/ApplicationShell";
import Button from "../components/Button";
import Dialog from "../components/Dialog";
import ResponsiveDataTable from "../components/ResponsiveDataTable";
import StatusBadge from "../components/StatusBadge";

const safeParsePackData = (value) => {
  if (!value) return {};
  if (typeof value === "object") return value;
  try {
    return JSON.parse(value);
  } catch {
    return {};
  }
};

const getModelId = (record) => record?.id || record?._id;
const getDraft = (record) => safeParsePackData(record?.data ?? record);
const formatDate = (value) => value && moment(value).isValid()
  ? moment(value).format("D MMM YYYY, HH:mm")
  : "Not specified";
const sectionLabels = {
  location: "Location",
  users: "Users",
  forms: "Forms",
  uploads: "Uploads",
};

const getErrorMessage = (error, fallback) => (
  error?.response?.data?.message || error?.data?.message || error?.message || fallback
);

const TempPacks = () => {
  const navigate = useNavigate();
  const [drafts, setDrafts] = useState([]);
  const [pageInfo, setPageInfo] = useState({ last_page: 1, total: 0 });
  const [query, setQuery] = useState({ page: 0, pageSize: 10, sortBy: "updated_at", sortOrder: "desc" });
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState("");
  const [draftToDelete, setDraftToDelete] = useState(null);

  const loadDrafts = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const response = await DraftPackService.getAllPacks(
        query.page,
        query.pageSize,
        query.sortBy,
        query.sortOrder,
        {}
      );
      const records = response?.data || [];
      if (records.length === 0 && query.page === 0) {
        navigate("/packs/create", { replace: true });
        return;
      }
      setDrafts(records);
      setPageInfo({
        last_page: Number(response?.last_page) || 1,
        total: Number(response?.total) || records.length,
      });
    } catch (requestError) {
      setError(getErrorMessage(requestError, "Draft packs could not be loaded."));
    } finally {
      setLoading(false);
    }
  }, [navigate, query.page, query.pageSize, query.sortBy, query.sortOrder]);

  useEffect(() => { loadDrafts(); }, [loadDrafts]);

  const columns = useMemo(() => [
    {
      key: "possession",
      header: "Possession reference",
      accessor: (record) => getDraft(record)?.location?.possession_no || "Not specified",
      sortable: false,
    },
    {
      key: "progress",
      header: "Current step",
      accessor: (record) => sectionLabels[getDraft(record)?.current_section] || "Location",
      render: (record) => <StatusBadge type="draft">{sectionLabels[getDraft(record)?.current_section] || "Location"}</StatusBadge>,
      sortable: false,
    },
    {
      key: "primary_es",
      header: "Primary ES",
      accessor: (record) => getDraft(record)?.users?.esusers?.[0]?.name || "Not assigned",
      sortable: false,
    },
    {
      key: "created_at",
      header: "Created",
      accessor: (record) => formatDate(record.created_at),
      sortValue: (record) => record.created_at,
    },
    {
      key: "updated_at",
      header: "Last updated",
      accessor: (record) => formatDate(record.updated_at),
      sortValue: (record) => record.updated_at,
    },
  ], []);

  const actions = useMemo(() => [
    { label: "Resume draft", onClick: (record) => navigate(`/packs/create/${getModelId(record)}`) },
    { label: "Delete draft", onClick: (record) => setDraftToDelete(record) },
  ], [navigate]);

  const confirmDelete = async () => {
    if (!draftToDelete) return;
    setDeleting(true);
    setError("");
    try {
      await DraftPackService.deletePack(getModelId(draftToDelete));
      setDraftToDelete(null);
      await loadDrafts();
    } catch (requestError) {
      setError(getErrorMessage(requestError, "The draft pack could not be deleted."));
    } finally {
      setDeleting(false);
    }
  };

  const handleTableQuery = useCallback(({ sortBy }) => {
    const sort = sortBy?.[0];
    setQuery((current) => {
      const nextSortBy = sort?.id || "updated_at";
      const nextSortOrder = sort?.desc ? "desc" : "asc";
      if (current.sortBy === nextSortBy && current.sortOrder === nextSortOrder) return current;
      return { ...current, page: 0, sortBy: nextSortBy, sortOrder: nextSortOrder };
    });
  }, []);


  return (
    <>
      <PageHeader
        actions={<Button icon={Plus} onClick={() => navigate("/packs/create")}>Start new pack</Button>}
        description="Continue an existing draft or start a new worksite pack."
        title="Create pack"
      />

      <div className="wsm-draft-packs">
        <Alert title="You have unfinished packs" type="info">
          Resume a draft to continue from its last completed step, or delete drafts you no longer need.
        </Alert>

        <ResponsiveDataTable
          actions={actions}
          caption="Draft packs"
          columns={columns}
          data={drafts}
          emptyAction={<Button icon={Plus} onClick={() => navigate("/packs/create")}>Start new pack</Button>}
          emptyDescription="You do not have any unfinished packs."
          emptyTitle="No draft packs"
          error={error}
          initialSort={{ key: "updated_at", direction: "desc" }}
          loading={loading}
          manual
          onPageChange={(page) => setQuery((current) => ({ ...current, page }))}
          onPageSizeChange={(pageSize) => setQuery((current) => ({ ...current, page: 0, pageSize }))}
          onQueryChange={handleTableQuery}
          onRetry={loadDrafts}
          pageCount={Math.max(pageInfo.last_page, 1)}
          pageIndex={query.page}
          pageSize={query.pageSize}
          pageSizeOptions={[10, 20, 50]}
          showColumnSelector={false}
          showCount={false}
          showExport={false}
          showFilters={false}
          showSearch={false}
          title="Draft packs"
          totalRows={pageInfo.total}
        />
      </div>

      <Dialog
        actions={(
          <>
            <Button disabled={deleting} onClick={() => setDraftToDelete(null)} variant="outline">Cancel</Button>
            <Button loading={deleting} loadingLabel="Deleting..." onClick={confirmDelete} variant="destructive">Delete draft</Button>
          </>
        )}
        description="This permanently removes the draft and its saved progress."
        onClose={() => !deleting && setDraftToDelete(null)}
        open={Boolean(draftToDelete)}
        size="small"
        title="Delete this draft pack?"
      >
        <p>
          Delete draft for <strong>{draftToDelete ? getDraft(draftToDelete)?.location?.possession_no || "an unspecified possession" : ""}</strong>?
        </p>
      </Dialog>
    </>
  );
};

export default TempPacks;
